import { Button } from '@studio/design-system/button';
import { AnimatePresence, motion, Motions } from '@studio/design-system/motion';
import { MultiSelect } from '@studio/design-system/multi-select';
import { Centered } from '@studio/design-system/overlay';
import { usePrompt } from '@studio/design-system/prompt';
import * as Select from '@studio/design-system/select';
import { Content, List, Root, Trigger } from '@studio/design-system/tabs';
import { clsx } from 'cn';
import { useState } from 'react';
import { post } from '../../../notifications/index.js';
import { normalizeEmail, emailsEqual } from '../../../utils/email.js';
import { LimitExceeded } from './components.jsx';
import { UserInviteDetails } from './invite-details.jsx';
import { InviteByLink } from './invite-link.jsx';
import { GuestsTab } from './tab-guests.jsx';
import type { User } from './types.js';
import T from '@studio/design-system/themes';

const valueMap: Record<string, string> = {
  viewer: 'Viewer',
  producer: 'Producer',
  admin: 'Admin'
};

const EmailsInput = ({
  disabled,
  maxUsers,
  onAdd
}: {
  disabled?: boolean;
  maxUsers?: number;
  onAdd: (users: User[]) => void;
}) => {
  const [emails, setEmails] = useState<{ label: string; value: string }[]>([
    {
      label: 'kishore@iris.com',
      value: normalizeEmail('kishore@iris.com')
    },
    {
      label: 'hunter@gmail.com',
      value: normalizeEmail('hunter@gmail.com')
    }
  ]);

  const canSendInvite = !disabled && emails.length !== 0;

  const [permission, setPermission] = useState<User['permission']>('viewer');

  return (
    <div className='flex items-center gap-x-24'>
      <div className='m-[1px] flex grow text-muted'>
        <MultiSelect
          className={clsx('grow', {
            'cursor-not-allowed': disabled
          })}
          disabled={!!disabled}
          maxItems={maxUsers}
          onChange={setEmails}
          onMaxItemsExceeded={() => {
            // Inline validation at the email field: the feedback is already at
            // the point of action, so it must not also light the bell.
            post(
              {
                title: "Can't add more collaborators",
                message: "You've reached the maximum number of collaborators",
                severity: 'error'
              },
              { autoClose: true }
            );
          }}
          validateInput={(v) => /^[\w.-]+@[\d.A-Za-z-]+\.[A-Za-z]{2,4}$/.test(v)}
          value={emails}
        />
        <div className='relative top-2'>
          <Select.Root
            onValueChange={(v) => {
              setPermission(v as User['permission']);
            }}
            value={permission}
            variant='light'
          >
            <Select.Trigger className='w-auto bg-transparent pr-0 text-black' disabled={disabled}>
              {valueMap[permission]}
            </Select.Trigger>
            <Select.Content align='end' position='popper' side='bottom'>
              <Select.Item className='h-auto' value='admin'>
                <div className='flex flex-col gap-4 py-8'>
                  <Select.ItemText>Admin</Select.ItemText>
                  <div className='text-md text-muted'>Can control all sources & invite users</div>
                </div>
              </Select.Item>
              <Select.Item className='h-auto' value='producer'>
                <div className='flex flex-col gap-4 py-8'>
                  <Select.ItemText>Producer</Select.ItemText>
                  <div className='text-md text-muted'>Can control all or specific sources</div>
                </div>
              </Select.Item>
              <Select.Item className='h-auto' value='viewer'>
                <div className='flex flex-col gap-4 py-8'>
                  <Select.ItemText>Viewer</Select.ItemText>
                  <div className='text-md text-muted'>Can view all sources, but not control</div>
                </div>
              </Select.Item>
            </Select.Content>
          </Select.Root>
        </div>
      </div>
      <Button
        className={clsx('mr-2 py-20', {
          'cursor-not-allowed': disabled
        })}
        color='primary'
        disabled={!canSendInvite}
        onClick={() => {
          // Normalize email addresses to ensure case-insensitive matching
          onAdd(
            emails.map((email) => ({
              email: normalizeEmail(email.value),
              permission,
              status: 'pending'
            }))
          );

          setEmails([]);
          setPermission('viewer');

          post({
            message: `Invitation successfully sent to "${emails
              .map((email) => email.value)
              .join('", "')}"`,
            title: 'Invitation sent!',
            severity: 'info'
          });
        }}
        size='lg'
      >
        Send Invite
      </Button>
    </div>
  );
};

/** UserInvite. */
export const UserInvite = () => {
  const prompt = usePrompt();
  const COLLABORATOR_LIMIT = 5;

  const [users, setUsers] = useState<User[]>([
    {
      email: normalizeEmail('noah@gmail.com'),
      permission: 'owner',
      status: 'accepted'
    },
    {
      email: normalizeEmail('kishore@gmail.com'),
      permission: 'viewer',
      status: 'pending'
    }
  ]);

  const collaboratorLimitExceeded = users.length >= COLLABORATOR_LIMIT;

  const guests = users.filter((v) => !!v.expiration);

  return (
    <div>
      <Centered className='bg-black backdrop-blur-sm'>
        <div className='h-screen max-h-[650px] w-[530px] rounded-12 border-t-12 border-primary bg-light p-32 pt-12 text-black'>
          <Root data-theme={T.getTheme('light')} defaultValue='users'>
            <List className='px-0'>
              <Trigger value='users'>Invite users</Trigger>
              <Trigger value='guests'>Guests</Trigger>
            </List>
            <div className='mt-12 h-full'>
              <Content value='users'>
                <EmailsInput
                  disabled={collaboratorLimitExceeded}
                  maxUsers={COLLABORATOR_LIMIT - users.length}
                  onAdd={(value) => {
                    // Use case-insensitive email comparison for duplicate detection
                    setUsers((currentUsers) => {
                      const allUsers = [...currentUsers, ...value];
                      const uniqueUsers: User[] = [];
                      const seenEmails = new Set<string>();

                      for (const user of allUsers) {
                        const normalizedEmail = normalizeEmail(user.email);
                        if (!seenEmails.has(normalizedEmail)) {
                          seenEmails.add(normalizedEmail);
                          uniqueUsers.push({
                            ...user,
                            email: normalizedEmail // Ensure stored email is normalized
                          });
                        }
                      }

                      return uniqueUsers;
                    });
                  }}
                />
                <div className='mt-18 flex flex-col gap-12'>
                  <InviteByLink />
                  {users.map((user) => (
                    <UserInviteDetails
                      key={user.email}
                      onDelete={() => {
                        prompt.confirm({
                          title: 'Remove user',
                          message: 'Are you sure you want to remove this user?',
                          onConfirm: (answer) => {
                            if (answer) {
                              setUsers(users.filter((u) => !emailsEqual(u.email, user.email)));
                            }
                          }
                        });
                      }}
                      onUpdate={(update: Partial<User>) => {
                        setUsers(
                          users.map((u) =>
                            emailsEqual(u.email, user.email) ? { ...u, ...update } : u
                          )
                        );
                      }}
                      user={user}
                    />
                  ))}
                </div>
                <AnimatePresence>
                  {!!collaboratorLimitExceeded && (
                    <motion.div {...Motions.Fade}>
                      <LimitExceeded className='mt-16' />
                    </motion.div>
                  )}
                </AnimatePresence>
              </Content>
              <Content value='guests'>
                <GuestsTab
                  guests={guests}
                  onDelete={(guestEmail: string) => () => {
                    prompt.confirm({
                      title: 'Remove guest',
                      message: 'Are you sure you want to remove this user?',
                      onConfirm: (answer) => {
                        if (answer) {
                          setUsers(users.filter((u) => !emailsEqual(u.email, guestEmail)));
                        }
                      }
                    });
                  }}
                  updateGuest={(email, update) => {
                    setUsers(
                      users.map((u) => (emailsEqual(u.email, email) ? { ...u, ...update } : u))
                    );
                  }}
                />
              </Content>
            </div>
          </Root>
        </div>
      </Centered>
    </div>
  );
};
